import type { NeonProvisionResult, VercelRunner } from '@cli-types/next-adapter/vercel.js'
import { PROVISION_TIMEOUT_MS } from '@cli-constants/timeouts.js'
import { spinner } from '@cli-utils/terminal.js'
import { runVercel } from '../runner/run-vercel.js'
import { neonAddArgs } from './neon-add-args.js'
import { readNeonProvisionInfo } from './read-neon-provision-info.js'

/**
 * On a fresh account `integration add neon` can require marketplace terms or a plan selection, so
 * this unattended path usually needs a `--plan` id to succeed.
 */
export async function provisionNeon(
  runner: VercelRunner,
  cwd: string,
  options: { plan?: string; env?: NodeJS.ProcessEnv }
): Promise<NeonProvisionResult> {
  const provisionSpinner = spinner()
  provisionSpinner.start('Creating your Neon database')

  const add = await runVercel(runner, neonAddArgs(options, 'json'), {
    cwd,
    mode: 'capture',
    timeoutMs: PROVISION_TIMEOUT_MS,
    env: options.env
  })

  if (!add.success) {
    provisionSpinner.stop('Neon provisioning did not complete')
    const combined = `${add.stdout}\n${add.stderr}`.trim()
    const detail = combined || undefined
    if (/terms/i.test(combined)) return { failure: 'terms-required', detail }
    if (/claim/i.test(combined)) return { failure: 'claim-required', detail }
    return { failure: add.timedOut ? 'timeout' : 'provision-failed', detail }
  }

  provisionSpinner.clear()
  return readNeonProvisionInfo(add.stdout)
}
